<template>
  <asd20-viewport v-if="facets" scrollable class="asd20-school-filters">
    <asd20-accordion
      :badge="selectedEducationLevels.length > 0 ? 0 : -1"
      opened
      expanded
      heading="Education Levels"
    >
      <asd20-checkbox-list
        :options="definitions.educationLevels"
        :checked-values="selectedEducationLevels"
        @input="$emit('update:selectedEducationLevels', $event)"
      />
    </asd20-accordion>

    <asd20-accordion
      :badge="maxDistance <= 25 && userLocation ? 0 : -1"
      expanded
      heading="Location"
    >
      <div class="location-form">
        <asd20-text-input
          id="addressInput"
          label="Address Input"
          autocomplete="on"
          name="address"
          :value="referenceAddress"
          :help-text="
            'Enter an address. You may need to include a city and state to get a more accurate result.'
          "
          style="margin-bottom: .5rem"
          onfocus="this.select()"
          onmouseup="return false"
          placeholder="Address City State Zip"
          @input="onAddressInput"
          @change.native="onManualInput"
          @blur.native="onManualInput"
        >
          <asd20-spinner
            v-if="locationPending"
            slot="prefix"
            style="flex-shrink: 0"
          />
        </asd20-text-input>
        <asd20-button
          class="current-location"
          slot="suffix"
          size="sm"
          icon="map"
          label="Use My Current Location"
          horizontal
          reversed
          @click.native="requestGeoLocation"
        />
        <asd20-slider-input
          :label="maxDistanceLabel"
          :value="maxDistance"
          :min="1"
          :max="26"
          @input="onMaxDistanceInput"
        />
        <asd20-button
          class="boundary-button"
          size="sm"
          icon="directions"
          link="https://www.schoolsitelocator.com/apps/academy20/"
          label="View the D20 Boundary Map"
          horizontal
          reversed
        />
      </div>
    </asd20-accordion>

    <asd20-accordion
      :badge="selectedEducationalAttributes.length > 0 ? 0 : -1"
      expanded
      heading="Academics &amp; Supports"
    >
      <asd20-checkbox-list
        :options="definitions.educationalItems"
        :checked-values="selectedEducationalAttributes"
        @input="$emit('update:selectedEducationalAttributes', $event)"
      />
    </asd20-accordion>

    <asd20-accordion
      :badge="selectedArtsOfferings.length > 0 ? 0 : -1"
      expanded
      heading="Arts Programs"
    >
      <asd20-checkbox-list
        :options="definitions.artsItems"
        :checked-values="selectedArtsOfferings"
        @input="$emit('update:selectedArtsOfferings', $event)"
      />
    </asd20-accordion>

    <asd20-accordion
      :badge="selectedAthleticsOfferings.length > 0 ? 0 : -1"
      expanded
      heading="Athletics Programs"
    >
      <asd20-checkbox-list
        :options="definitions.athleticsItems"
        :checked-values="selectedAthleticsOfferings"
        @input="$emit('update:selectedAthleticsOfferings', $event)"
      />
    </asd20-accordion>
  </asd20-viewport>
</template>

<script>
import { get, sync } from 'vuex-pathify'
import debounce from 'lodash.debounce'
import definitions from '@/data/definitions'
import Asd20Spinner from '@asd20/ui/src/components/atoms/Asd20Spinner'
import Asd20Button from '@asd20/ui/src/components/atoms/Asd20Button'
import Asd20Accordion from '@asd20/ui/src/components/molecules/Asd20Accordion'
import Asd20CheckboxList from '@asd20/ui/src/components/molecules/Asd20CheckboxList'
import Asd20SliderInput from '@asd20/ui/src/components/molecules/Asd20SliderInput'
import Asd20Viewport from '@asd20/ui/src/components/atoms/Asd20Viewport'
import Asd20TextInput from '@asd20/ui/src/components/molecules/Asd20TextInput'

export default {
  name: 'Asd20SchoolFilters',
  components: {
    Asd20Spinner,
    Asd20Accordion,
    Asd20Button,
    Asd20CheckboxList,
    Asd20Viewport,
    Asd20SliderInput,
    Asd20TextInput,
  },
  props: {
    view: { type: String, default: 'list' },
    maxDistance: { type: Number, default: 0 },
    selectedEducationLevels: { type: Array, default: () => [] },
    selectedEducationalAttributes: { type: Array, default: () => [] },
    selectedArtsOfferings: { type: Array, default: () => [] },
    selectedAthleticsOfferings: { type: Array, default: () => [] },
    facets: { type: Object, default: null },
  },
  data: () => ({
    locationPending: false,
  }),
  computed: {
    referenceAddress: sync('location/referenceAddress'),
    geoLocationAvailable: get('location/geoLocationAvailable'),
    geoCodedAddress: sync('location/geoCodedAddress'),
    userLocation: get('location/userLocation'),

    maxDistanceLabel() {
      return this.maxDistance > 25
        ? 'Distance from location'
        : `Within ${this.maxDistance} miles of my location`
    },

    definitions() {
      // Return each definition category and items with count added
      // First map by category
      if (!this.facets) return
      return {
        educationLevels: definitions.educationLevels.map(d => {
          let facet = this.facets.educationLevels.find(f => f.value === d.value)
          // let count = this.facets.educationLevels.find(f => f.value === d.value).length
          let count = !!facet ? facet.count : 0
          return {
            ...d,
            description: '',
            count,
          }
        }),
        educationalItems: definitions.educationalItems.map(d => {
          let facet = this.facets.educationalAttributes.find(
            f => f.value === d.value
          )
          let count = !!facet ? facet.count : 0
          return {
            ...d,
            description: '',
            count,
          }
        }),
        athleticsItems: definitions.athleticsItems.map(d => {
          let facet = this.facets.athleticsOfferings.find(
            f => f.value === d.value
          )
          let count = !!facet ? facet.count : 0
          return {
            ...d,
            description: '',
            count,
          }
        }),
        artsItems: definitions.artsItems.map(d => {
          let facet = this.facets.artsOfferings.find(f => f.value === d.value)
          let count = !!facet ? facet.count : 0
          return {
            ...d,
            description: '',
            count,
          }
        }),
      }
    },
  },
  methods: {
    geoCodeAddress: debounce(async function() {
      if (!this.locationPending) {
        this.locationPending = true
        const result = await this.$store.dispatch('location/geoCodeAddress')
        this.locationPending = false

        if (Array.isArray(result) && result.length > 0) {
          // Switch to map view if geocoding returned a valid result
          this.$emit('update:view', 'map')
        }
      }
    }, 1000),

    async onAddressInput(value) {
      this.geoCodedAddress = ''
      this.referenceAddress = value
      this.geoCodeAddress()
    },
    onMaxDistanceInput(value) {
      this.$emit('update:maxDistance', Number(value))
    },
    async requestGeoLocation() {
      this.locationPending = true

      try {
        // Always use the device's current location
        await this.$store.dispatch('location/requestGeoLocation')

        // Get the friendly string version of the location
        await this.$store.dispatch('location/lookupAddress')

        // Update the referenceAddress with the reverse-geocoded string
        this.referenceAddress = this.geoCodedAddress

        // Switch to map view
        this.$emit('update:view', 'map')
      } catch (e) {
        console.error('Failed to get current location:', e)
      } finally {
        this.locationPending = false
      }
    },
    onAddressInput(value) {
      this.geoCodedAddress = ''
      this.referenceAddress = value
      this.geoCodeAddress()
    },
    onManualInput(event) {
      const value = event.target.value
      if (value !== this.referenceAddress) {
        this.onAddressInput(value)
      }
    },

    // async requestGeoLocation() {
    //   this.locationPending = true
    //   if (this.referenceAddress) {
    //     await this.$store.dispatch('location/geoCodeAddress')
    //   } else {
    //     await this.$store.dispatch('location/requestGeoLocation')
    //     await this.$store.dispatch('location/lookupAddress')
    //     this.referenceAddress = this.geoCodedAddress
    //   }
    //   this.$emit('update:view', 'map')
    //   this.locationPending = false
    // },
    // redirectToMap() {
    //   window.location.href = 'https://www.schoolsitelocator.com/apps/academy20/'
    // },
  },
}
</script>

<style lang="scss">
@import '@asd20/ui/src/design/_variables.scss';
.asd20-viewport {
  flex-grow: 0 !important;
}
input::selection {
  background: var(--color__accent-t50) !important;
}
.asd20-school-filters {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-bottom: 1px solid var(--color__tertiary);

  // flex-grow: 1;

  .asd20-viewport {
    margin-bottom: auto;
  }

  .asd20-checkbox {
    width: auto;
    padding-left: space(1);
    padding-right: space(1);
  }

  .asd20-checkbox-list .asd20-checkbox-list {
    background-color: #f4f5f6;
  }

  .asd20-checkbox-list .nested-checkbox {
    padding-left: 0;
  }

  .location-form {
    display: flex;
    flex-direction: column;
    padding: space(1);
    .boundary-button {
      padding: 0.5rem;
      margin-top: 2rem;
      width: 100%;
      justify-content: center;
      .asd20-icon {
        color: white;
      }
    }
    .current-location {
      padding: 0.5rem;
      margin-top: 0.5rem;
      margin-bottom: 1rem;
      width: 100%;
      justify-content: center;
      .asd20-icon {
        color: white;
      }
    }
    .input-wrapper {
      flex-direction: column;
      align-items: flex-end;
    }

    .asd20-spinner {
      position: absolute;
      top: 0.2rem;
      left: 45%;
      height: 2rem;
      width: 2rem;
    }
    .asd20-text-input input {
      margin: 0;
      padding: 0.625rem;
      background: var(--color__accent-t90);
    }
    .asd20-text-input input::placeholder {
      font-size: 1rem;
    }

    .asd20-slider-input input:focus {
      box-shadow: none;
      outline: none;
    }
  }
}
</style>
